import { lazy, Suspense, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PopupCancelledError, useAuth0 } from '@auth0/auth0-react';
import { KeyRound, X } from 'lucide-react';
import { forceLogout } from '@/auth/force-logout';
import {
  SESSION_IDLE_MS,
  getScreenLockState,
  lastActivityAt,
  screenLockMs,
  startScreenLock,
  subscribeScreenLock,
  unlockScreen,
} from '@/auth/screen-lock';
import { NexaLockupLight } from '@/components/nexa-mark/nexa-lockup-light';
import { Button } from '@/components/ui/button';
import { createWakeGesture } from './wake-gesture';

// The screen lock (state: `auth/screen-lock.ts`). While locked the console is `inert`
// under a full-screen screensaver; a click, a key, or a deliberate mouse movement (never
// a nudge — `wake-gesture.ts`) brings up the unlock card. Unlocking re-authenticates at Auth0 with `prompt=login`: the operator
// types their password on Auth0's page — the console never sees or checks it. A popup
// keeps every open screen intact; when the popup cannot open, the same sign-in runs as
// a full-page redirect that lands back where the operator was, unlocked.

const Screensaver = lazy(() =>
  import('@/components/screensaver/screensaver').then((m) => ({ default: m.Screensaver })),
);

/** Pages where there is no session to protect yet. */
const AUTH_ROUTES = ['/login', '/callback'];
/** How long the unlock card waits for the operator before the screensaver returns. */
const PROMPT_IDLE_MS = 45_000;

export function ScreenLock() {
  const { isAuthenticated, user } = useAuth0();
  const sub = user?.sub ?? null;
  const lock = useSyncExternalStore(subscribeScreenLock, getScreenLockState, getScreenLockState);

  useEffect(() => {
    if (!isAuthenticated) return;
    return startScreenLock({ lockMs: screenLockMs(), sub });
  }, [isAuthenticated, sub]);

  if (!isAuthenticated || !lock.locked) return null;
  if (AUTH_ROUTES.some((p) => window.location.pathname.startsWith(p))) return null;
  return createPortal(<LockOverlay />, document.body);
}

type Failure = 'cancelled' | 'failed';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '?';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : undefined;
  return `${first}${last ?? ''}`.toUpperCase();
}

function LockOverlay() {
  const { t } = useTranslation('screensaver');
  const { user, loginWithPopup, loginWithRedirect, getIdTokenClaims } = useAuth0();
  const [prompt, setPrompt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const lockedSub = useRef(user?.sub ?? null);
  const unlockButton = useRef<HTMLButtonElement>(null);
  const signingOut = useRef(false);
  const backdrop = useRef<HTMLDivElement>(null);

  // Back to the screensaver: the card's X, Escape, a click around the card, or time.
  const dismiss = useCallback((): void => {
    setPrompt(false);
    setFailure(null);
  }, []);

  const name = user?.name ?? user?.email ?? '';
  const email = user?.email ?? '';
  const loginHint = email ? { login_hint: email } : {};

  // The console underneath is out of reach: no focus, no clicks, no screen reader.
  useEffect(() => {
    const app = document.getElementById('root');
    if (!app) return;
    app.inert = true;
    return () => {
      app.inert = false;
    };
  }, []);

  // The session's own idle limit still applies behind the lock (operator-ui-tds §7):
  // input on the lock screen is not use of the console, so it cannot keep a session
  // alive that nobody is working in.
  useEffect(() => {
    let timer = 0;
    const check = (): void => {
      const left = lastActivityAt() + SESSION_IDLE_MS - Date.now();
      if (left > 0) {
        timer = window.setTimeout(check, left);
        return;
      }
      if (signingOut.current) return;
      signingOut.current = true;
      void forceLogout({ reason: 'idle-timeout' });
    };
    check();
    return () => window.clearTimeout(timer);
  }, []);

  // Screensaver → unlock card on a click / tap, a key, or a deliberate mouse movement.
  useEffect(() => {
    if (prompt) return;
    const gesture = createWakeGesture();
    const wake = (): void => setPrompt(true);
    const onMove = (e: PointerEvent): void => {
      if (gesture.move(e.clientX, e.clientY, e.timeStamp)) wake();
    };
    const opts = { capture: true, passive: true } as const;
    const wakers = ['keydown', 'pointerdown'] as const;
    for (const ev of wakers) window.addEventListener(ev, wake, opts);
    window.addEventListener('pointermove', onMove, opts);
    return () => {
      for (const ev of wakers) window.removeEventListener(ev, wake, opts);
      window.removeEventListener('pointermove', onMove, opts);
    };
  }, [prompt]);

  // Unlock card → screensaver after a while, or straight away on Escape.
  useEffect(() => {
    if (!prompt || busy) return;
    unlockButton.current?.focus();
    let timer = window.setTimeout(dismiss, PROMPT_IDLE_MS);
    const reset = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(dismiss, PROMPT_IDLE_MS);
    };
    const onKey = (e: KeyboardEvent): void => (e.key === 'Escape' ? dismiss() : reset());
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointermove', reset, { passive: true });
    window.addEventListener('pointerdown', reset, { passive: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointermove', reset);
      window.removeEventListener('pointerdown', reset);
    };
  }, [prompt, busy, dismiss]);

  // A click around the card closes it only if it also STARTED there: the click that woke
  // the screensaver is released over the backdrop too, and must not shut the card it opened.
  // (Keyboard users have the X and Escape.)
  useEffect(() => {
    const el = backdrop.current;
    if (!prompt || !el) return;
    let pressed = false;
    const down = (e: PointerEvent): void => {
      pressed = e.target === el;
    };
    const click = (e: MouseEvent): void => {
      if (pressed && e.target === el) dismiss();
      pressed = false;
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('click', click);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('click', click);
    };
  }, [prompt, dismiss]);

  const unlock = async (): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await loginWithPopup({ authorizationParams: { prompt: 'login', ...loginHint } });
      const claims = await getIdTokenClaims();
      unlockScreen();
      // Someone else signed in at this desk: start the console over as them rather than
      // hand them the previous operator's open screens.
      if (claims?.sub && lockedSub.current && claims.sub !== lockedSub.current) {
        window.location.assign('/');
      }
    } catch (err) {
      setFailure(err instanceof PopupCancelledError ? 'cancelled' : 'failed');
    } finally {
      setBusy(false);
    }
  };

  const continueOnPage = (): void => {
    void loginWithRedirect({
      authorizationParams: { prompt: 'login', ...loginHint },
      appState: { returnTo: `${window.location.pathname}${window.location.search}`, unlockScreen: true },
    });
  };

  const signOut = (): void => {
    signingOut.current = true;
    void forceLogout({ reason: 'user-initiated' });
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-[#040D1A]" data-testid="screen-lock">
      <Suspense fallback={null}>
        <Screensaver className="h-full w-full" />
      </Suspense>
      {prompt ? (
        <div
          ref={backdrop}
          className="absolute inset-0 z-10 grid place-items-center overflow-y-auto bg-[#040D1A]/60 px-4 py-8 backdrop-blur-md"
          data-testid="screen-lock-backdrop"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="screen-lock-title"
            className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#07213F]/95 p-6 text-[#EEF4FA] shadow-2xl"
          >
            <button
              type="button"
              onClick={dismiss}
              aria-label={t('lock.close')}
              title={t('lock.close')}
              className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-full text-[#C9D6E5] transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7FDDF7]"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
            <NexaLockupLight size="card" />
            <h2 id="screen-lock-title" className="mt-6 text-xl font-semibold tracking-tight">
              {t('lock.title')}
            </h2>

            <div className="mt-5 flex items-center gap-3 rounded-xl bg-white/5 p-3">
              <span className="grid h-10 w-10 flex-none place-items-center rounded-full bg-[#0171B7] text-sm font-semibold">
                {initialsOf(name)}
              </span>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium" data-testid="screen-lock-name">
                  {name}
                </div>
                {email && email !== name ? <div className="truncate text-xs text-[#C9D6E5]">{email}</div> : null}
              </div>
            </div>

            <Button
              ref={unlockButton}
              size="lg"
              onClick={() => void unlock()}
              disabled={busy}
              className="mt-5 w-full bg-[#05B8EC] text-[#07213F] hover:bg-[#2CC5F0] focus-visible:ring-[#7FDDF7] focus-visible:ring-offset-[#07213F]"
            >
              <KeyRound className="h-4 w-4" aria-hidden />
              {busy ? t('lock.waiting') : t('lock.unlock')}
            </Button>

            {failure ? (
              <div role="alert" className="mt-4 rounded-lg bg-[#FF4D5E]/15 p-3 text-sm leading-relaxed">
                {t(`lock.failure.${failure}`)}
                {failure === 'failed' ? (
                  <button
                    type="button"
                    onClick={continueOnPage}
                    className="mt-2 block font-semibold text-white underline underline-offset-4"
                  >
                    {t('lock.continueOnPage')}
                  </button>
                ) : null}
              </div>
            ) : null}

            <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-white/10 pt-4 text-sm">
              <span className="text-[#C9D6E5]">{t('lock.notYou', { name })}</span>
              <button
                type="button"
                onClick={signOut}
                className="rounded font-semibold text-white underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7FDDF7]"
              >
                {t('lock.signOut')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
